import { useState } from 'react'
import { motion } from 'framer-motion'
import { LogIn, ChevronRight } from 'lucide-react'
import { Button, ButtonVariant } from '@meiuca/ui-components'
import { AuthModal } from './AuthModal'

interface LoginRequiredEmptyStateProps {
  title?: string
  description?: string
  buttonText?: string
}

/**
 * Empty state shown on authenticated pages when the user is logged out.
 * Replaces the generic "failed to load" error that a 401 would otherwise
 * produce. Opens the AuthModal in place; on success the auth context updates
 * and the host page re-renders to load its data.
 */
export function LoginRequiredEmptyState({
  title = 'Entre na sua conta',
  description = 'Faça login para acessar esta página.',
  buttonText = 'Fazer login'
}: LoginRequiredEmptyStateProps) {
  const [authModalOpen, setAuthModalOpen] = useState(false)

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center justify-center py-16 px-6 text-center bg-background-white rounded-xl border border-border-primary"
      >
        <div className="w-20 h-20 bg-brand-primary/10 rounded-full flex items-center justify-center mb-6">
          <LogIn className="w-10 h-10 text-brand-primary" />
        </div>

        <h2 className="text-xl font-semibold text-text-primary mb-3">
          {title}
        </h2>

        <p className="text-text-secondary mb-8 max-w-md leading-relaxed">
          {description}
        </p>

        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={() => setAuthModalOpen(true)}
          className="flex items-center space-x-2"
        >
          <LogIn className="w-5 h-5" />
          <span>{buttonText}</span>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </motion.div>

      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => setAuthModalOpen(false)}
      />
    </>
  )
}
